import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import {
  Search, ChevronDown, ChevronRight, ChevronLeft, ArrowRight, ArrowUpRight,
  Bot, Factory, BookOpen, Package, Database, Cpu, Cloud, Code2, Rocket, Users,
  FlaskConical, ShoppingBag, Sparkles, Orbit, ClipboardCheck, Award,
  GraduationCap, Briefcase, Stethoscope, UtensilsCrossed, TrendingUp, Trophy,
} from "lucide-react";
import SearchBox from "./components/SearchBox";
import DesktopSearch from "./components/DesktopSearch";
import { productAPI, categoryAPI } from "./services/api";
import { isMovedToAISolutions } from "./utils/aiSolutionMoves";
import { serviceTabPath } from "./utils/serviceTabs";
import Headerlogo from "./Images/Header_logo.webp";
// Menu imagery: small (720px) copies made for the nav, not the full-size page art.
import imgSvcCore from "./Images/nav/svc-core.webp";
import imgSvcIndustry from "./Images/nav/svc-industry.webp";
import imgSvcData from "./Images/nav/svc-data.webp";
import imgSvcDx from "./Images/nav/svc-dx.webp";
import imgSvcProduct from "./Images/nav/svc-product.webp";
import imgSvcTalent from "./Images/nav/svc-talent.webp";
import imgIndManufacturing from "./Images/nav/ind-bg.webp";
import imgIndChemical from "./Images/nav/ind-chem.webp";
import imgIndConsumer from "./Images/nav/ind-consumer.webp";
import imgAiBestoFlow from "./Images/nav/ai-bestoflow.webp";
import imgAiAskochat from "./Images/nav/ai-askochat.webp";
import imgAiJyotish from "./Images/nav/ai-jyotish.webp";
import imgInsBlogs from "./Images/nav/ins-blogs.webp";
import imgInsCases from "./Images/nav/ins-case.webp";
import imgInsStories from "./Images/nav/careers.webp";
import "./Header.css";

// "Category Rail" header. One nav model, three presentations (Header.css):
//   >= 1200px  full bar; hover (or tap) opens a mega panel per section:
//              category rail | detail with link cards | image
//   768-1199   compact bar; menu button opens a full-height sheet with three
//              panes side by side: sections | categories | detail
//   < 768      compact bar; sheet shows sections, then categories + detail
//              slide in for the chosen section
//
// Navigation model. No "Home" item — the logo links to "/". Items with a
// `menu` get a mega panel (desktop) / sheet section (compact); items without
// one are plain links. Every path here must be a real route in App.js.
// Order follows B2B convention (offerings → audiences → knowledge → company).
// Each column (category) has a short `desc` and an `img` for the detail pane;
// `desc` on an item renders as a secondary line on its link card.

// CMS products in this category are listed under the AI Solutions menu
// instead of the Products menu — see `aiProductItems`.
const AI_PRODUCT_CATEGORY = "AI";
const API_URL = process.env.REACT_APP_API_URL || "";
const assetUrl = (p) => (p ? `${API_URL}${p}` : null);

const DESK_MQ = "(min-width: 1200px)";
const TABLET_MQ = "(min-width: 768px) and (max-width: 1199px)";
const CTA = { label: "Talk to Expert", path: "/contact-us" };
const CONTACT = { email: "contact@bestowalsystems.com", phone: "+91 94055 30011" };

// The CMS only stores product category names; icon + one-line summary per
// known category (unknown categories fall back to a package icon, no summary).
const PRODUCT_CATEGORY_META = {
  CRM: { icon: Users, desc: "Customer relationship management." },
  Education: { icon: GraduationCap, desc: "Learning, assessment and upskilling platforms." },
  "HR & Business": { icon: Briefcase, desc: "Recruitment, manufacturing, vendor and IT asset management." },
  Healthcare: { icon: Stethoscope, desc: "Clinic management for modern healthcare." },
  Hospitality: { icon: UtensilsCrossed, desc: "Restaurant management: POS, tables and kitchen." },
  Career: { icon: TrendingUp, desc: "Jobs and skills for job seekers and employers." },
  Sports: { icon: Trophy, desc: "Sports and tournament management." },
  [AI_PRODUCT_CATEGORY]: { icon: Package, desc: "AI-powered products built by Bestowal." },
};
const productColumn = (title, items, cat = title) => {
  const meta = PRODUCT_CATEGORY_META[cat] || {};
  return { title, icon: meta.icon || Package, desc: meta.desc, img: items.find((p) => p.img)?.img, items };
};

const navLinks = [
  {
    // Mirrors the Services detail pages exactly: each core service is a
    // category and its tabs are the items, each at its own URL
    // (/<category>/<slug>, see utils/serviceTabs.js) — so they match on path.
    name: "Services",
    path: "/bestoservice",
    menu: {
      landingLabel: "View all services",
      blurb: "SAP, AI, data, cloud and engineering services, delivered end to end.",
      columns: [
        {
          title: "Digital Core Transformation",
          icon: Database,
          img: imgSvcCore,
          desc: "Unlock real-time business agility with SAP S/4HANA and SAP IBP.",
          items: [
            { name: "SAP S/4HANA Solutions", path: serviceTabPath("/digital-core-transformation", "sap") },
            { name: "SAP IBP Solutions", path: serviceTabPath("/digital-core-transformation", "ibp") },
          ],
        },
        {
          title: "Intelligent Industry Solution",
          icon: Cpu,
          img: imgSvcIndustry,
          desc: "Power your operations with AI, IoT and Industry 4.0 / 5.0 innovation.",
          items: [
            { name: "Industry 4.0 / 5.0 Solutions", path: serviceTabPath("/intelligent-industry-solutions", "industry") },
            { name: "AI/ML Solutions", path: serviceTabPath("/intelligent-industry-solutions", "aiml") },
          ],
        },
        {
          title: "Data & Cloud Transformation",
          icon: Cloud,
          img: imgSvcData,
          desc: "Turn your data into a strategic asset and scale effortlessly in the cloud.",
          items: [
            { name: "Data Engineering Solutions", path: serviceTabPath("/data-cloud-transformation", "data") },
            { name: "Cloud Transformation Services", path: serviceTabPath("/data-cloud-transformation", "cloud") },
          ],
        },
        {
          title: "Digital Experience & Engagement",
          icon: Code2,
          img: imgSvcDx,
          desc: "Create digital experiences that engage, convert and retain.",
          items: [
            { name: "Web Development Services", path: serviceTabPath("/digital-experience-engagement", "web") },
            { name: "App Development Solutions", path: serviceTabPath("/digital-experience-engagement", "mobile") },
          ],
        },
        {
          title: "Product Engineering & Innovation",
          icon: Rocket,
          img: imgSvcProduct,
          desc: "Accelerate digital product delivery from concept to launch.",
          items: [
            { name: "Product Engineering", path: serviceTabPath("/product-engineering-innovation", "product") },
          ],
        },
        {
          title: "Flexible Talent & Delivery Models",
          icon: Users,
          img: imgSvcTalent,
          desc: "Extend your team with top-tier tech talent, on your terms.",
          items: [
            { name: "IT Staff Augmentation Services", path: serviceTabPath("/flexible-talent-delivery-models", "staff") },
          ],
        },
      ],
    },
  },
  {
    name: "Industries",
    path: "/industry",
    menu: {
      landingLabel: "View all industries",
      blurb: "SAP and digital delivery for 14 industries.",
      // The same 14 industries the /industry landing page shows, grouped into
      // three categories (the landing itself is a flat tab row).
      columns: [
        {
          title: "Manufacturing & Engineering",
          icon: Factory,
          img: imgIndManufacturing,
          desc: "Six industrial sectors, from aeronautics to packaging.",
          items: [
            { name: "Manufacturing", path: "/manufacturing" },
            { name: "Aeronautical", path: "/aeronautical" },
            { name: "Electronics", path: "/electronics" },
            { name: "Semiconductor", path: "/semiconductor" },
            { name: "Packaging", path: "/packaging" },
            { name: "Automobile", path: "/automobile" },
          ],
        },
        {
          title: "Chemical & Pharma",
          icon: FlaskConical,
          img: imgIndChemical,
          desc: "Chemical, agrochemical and pharmaceutical producers.",
          items: [
            { name: "Chemical", path: "/chemical" },
            { name: "Agrochemical", path: "/agrochemical" },
            { name: "Pharmaceutical", path: "/pharmaceutical" },
          ],
        },
        {
          title: "Consumer & Services",
          icon: ShoppingBag,
          img: imgIndConsumer,
          desc: "Retail, FMCG, fintech, education and sports.",
          items: [
            { name: "Retail", path: "/retail" },
            { name: "FMCG", path: "/FMCG" },
            { name: "Fintech", path: "/fintech" },
            { name: "Education / EdTech", path: "/education-edtech" },
            { name: "Sports", path: "/sports" },
          ],
        },
      ],
    },
  },
  {
    name: "AI Solutions",
    path: "/AI-Machine-Learning-Solution",
    menu: {
      landingLabel: "Explore AI solutions",
      blurb: "AI products built in-house by Bestowal.",
      // Product thumbnails are 16:9 posters with text near every edge, so the
      // media box keeps their full frame instead of cropping to 4:3.
      wideMedia: true,
      // The same 5 products the AI landing page shows (its spellings and
      // taglines), grouped into three categories. CMS products in the "AI"
      // category are appended as an extra column at render (resolvedNavLinks).
      columns: [
        {
          title: "Enterprise AI",
          icon: Bot,
          img: imgAiBestoFlow,
          desc: "AI that plugs into SAP planning and plant operations.",
          items: [
            { name: "BestoFlow", path: "/bestoflow", desc: "Approval workflow for SAP IBP" },
            { name: "PredoSolution", path: "/predosolutions", desc: "Predictive maintenance engine" },
          ],
        },
        {
          title: "Generative AI",
          icon: Sparkles,
          img: imgAiAskochat,
          desc: "Chat and video generation on your own content.",
          items: [
            { name: "AskOchat", path: "/askochat", desc: "RAG-based AI chatbot" },
            { name: "Zenetick-AI", path: "/zentick_ai", desc: "Text and image to video generator" },
          ],
        },
        {
          title: "Lifestyle AI",
          icon: Orbit,
          img: imgAiJyotish,
          desc: "Vedic astrology and Kundali insights, powered by AI.",
          items: [
            { name: "Besto Jyotish", path: "/besto_jyotish", desc: "AI-powered Vedic astrology platform" },
          ],
        },
      ],
    },
  },
  { name: "Products", path: "/products" },
  {
    name: "Insights",
    path: "/blogs",
    menu: {
      landingLabel: "All insights",
      blurb: "Blogs, podcasts, case studies and client stories.",
      // The same 7 sections of the /blogs Insights hub, grouped into three
      // categories. `tab` is the hub's ?tab= key (exact active-highlighting);
      // Testimonials & Success Stories link to their full standalone pages.
      columns: [
        {
          title: "Knowledge Hub",
          icon: BookOpen,
          img: imgInsBlogs,
          desc: "Blogs, podcasts and company news.",
          items: [
            { name: "Blogs", path: "/blogs", tab: "blogs", default: true },
            { name: "Podcasts", path: "/blogs?tab=podcasts", tab: "podcasts" },
            { name: "News", path: "/blogs?tab=news", tab: "news" },
          ],
        },
        {
          title: "Case Studies & POC",
          icon: ClipboardCheck,
          img: imgInsCases,
          desc: "Delivered projects and proofs of concept.",
          items: [
            { name: "Case Studies", path: "/blogs?tab=caseStudies", tab: "caseStudies" },
            { name: "POC", path: "/blogs?tab=poc", tab: "poc" },
          ],
        },
        {
          title: "Client Stories",
          icon: Award,
          img: imgInsStories,
          desc: "What clients say about working with us.",
          items: [
            { name: "Testimonials", path: "/testimonials" },
            { name: "Success Stories", path: "/success-stories" },
          ],
        },
      ],
    },
  },
  { name: "About Us", path: "/about" },
  { name: "Careers", path: "/careerlanding" },
];

// matchMedia as state (re-renders when the breakpoint is crossed).
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

export default function Header() {
  const location = useLocation();
  const isDesk = useMediaQuery(DESK_MQ);
  const isTablet = useMediaQuery(TABLET_MQ);

  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // desktop: section whose mega panel is open
  const [panelMenu, setPanelMenu] = useState(null); // desktop: last opened section (kept while the panel fades out)
  const [catSel, setCatSel] = useState({}); // selected category index per section (panel + sheet)
  const [isSearchOpen, setIsSearchOpen] = useState(false); // desktop search sheet
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false); // compact top-bar search row
  const [sheetOpen, setSheetOpen] = useState(false); // compact menu sheet
  const [sheetLevel, setSheetLevel] = useState("root"); // phones: "root" (sections) or "sec" (a section)
  const [sheetSection, setSheetSection] = useState(null); // section shown in the sheet

  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const navRef = useRef(null);
  const indRef = useRef(null);
  const panelRef = useRef(null);
  const burgerRef = useRef(null);
  const itemRefs = useRef({}); // desktop nav tabs by name (indicator + focus return)
  const rowRefs = useRef({}); // sheet section rows by name (focus return)
  const sheetRefs = useRef({}); // sheet sections by name
  const openT = useRef(null);
  const closeT = useRef(null);
  const catT = useRef(null);
  const lastPointer = useRef("mouse");

  // Show the correct keyboard hint for search (⌘K on Mac, Ctrl K elsewhere).
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

  // Exact active-check for menu items pointing at a ?tab= inside a tabbed page
  // (the /blogs Insights hub): active only when on that page AND that tab.
  // `default: true` on an item marks the tab the page
  // opens with when no ?tab= is present. Plain items match on their path.
  const urlTab = new URLSearchParams(location.search).get("tab");
  const pathIs = (p) => location.pathname.toLowerCase() === p.split("?")[0].toLowerCase();
  const isItemActive = (item) => {
    // CMS products also open at their canonical /products/:slug path.
    if (item.slug && location.pathname === `/products/${item.slug}`) return true;
    if (!item.tab) return pathIs(item.path);
    if (location.pathname !== item.path.split("?")[0]) return false;
    return urlTab ? urlTab === item.tab : !!item.default;
  };

  // "Products" menu comes from the CMS — the same lean cards endpoint + product
  // categories the Home "In-House Products" section uses. Products are grouped
  // by category (one column per category, like the Home filter tabs).
  // Until it loads (or if it fails/is empty), Products stays a plain link.
  const [productItems, setProductItems] = useState([]);
  const [productCats, setProductCats] = useState([]);
  useEffect(() => {
    let active = true;
    productAPI
      .getCards()
      .then((res) => {
        if (!active) return;
        setProductItems(
          (res.data || [])
            // Products that moved to AI Solutions are in its static columns above.
            .filter((p) => (p.link || p.slug) && !isMovedToAISolutions(p))
            .map((p) => {
              // Link to the canonical /products/:slug page when there is one.
              // Otherwise CMS links may use the legacy tab form
              // ("/products?tab=Besto HRMS"). Those all share the /products
              // pathname, so extract the tab so isItemActive() can highlight
              // only the tab actually open.
              const path = p.slug && !p.isExternal ? `/products/${p.slug}` : p.link;
              const [, query] = path.split("?");
              const tab = query ? new URLSearchParams(query).get("tab") : null;
              return {
                name: p.title,
                path,
                slug: p.slug,
                external: p.isExternal,
                desc: p.cardDescription,
                logo: assetUrl(p.cardLogo),
                img: assetUrl(p.heroImage),
                categories: p.categories || [],
                ...(tab ? { tab } : {}),
              };
            })
        );
      })
      .catch(() => {});
    categoryAPI
      .getByType("products")
      .then((res) => {
        if (active) setProductCats((res.data || []).map((c) => c.name));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // AI-category products move to the AI Solutions menu; the rest stay in Products.
  const aiProductItems = productItems.filter((p) => p.categories.includes(AI_PRODUCT_CATEGORY));
  const menuProductItems = productItems.filter((p) => !p.categories.includes(AI_PRODUCT_CATEGORY));

  // Group products by category, mirroring the Home filter: only categories that
  // actually contain a product get a column; products whose categories are all
  // empty/unknown are collected under "More Products"; if no categories exist
  // at all, everything sits in a single "Our In-House Products" column.
  const productColumns = (() => {
    if (!menuProductItems.length) return [];
    const usedCats = productCats.filter((cn) => menuProductItems.some((p) => p.categories.includes(cn)));
    if (!usedCats.length) return [productColumn("Our In-House Products", menuProductItems)];
    const columns = usedCats.map((cn) => productColumn(cn, menuProductItems.filter((p) => p.categories.includes(cn))));
    const uncategorized = menuProductItems.filter((p) => !p.categories.some((cn) => usedCats.includes(cn)));
    if (uncategorized.length) columns.push(productColumn("More Products", uncategorized));
    return columns;
  })();

  // Nav model with CMS-driven sections resolved in: once the product cards
  // load, Products gains a menu of category columns, and AI Solutions gains an
  // "AI Products" column for the AI-category products.
  const resolvedNavLinks = navLinks.map((l) => {
    if (l.name === "Products" && productColumns.length) {
      return {
        ...l,
        menu: { landingLabel: "All products", blurb: "In-house products built and run by Bestowal.", columns: productColumns },
      };
    }
    if (l.name === "AI Solutions" && aiProductItems.length) {
      return {
        ...l,
        menu: { ...l.menu, columns: [...l.menu.columns, productColumn("AI Products", aiProductItems, AI_PRODUCT_CATEGORY)] },
      };
    }
    return l;
  });
  const menuLinks = resolvedNavLinks.filter((l) => l.menu);

  /* ---------- section (tab) active state ---------- */
  const industryRoutes = [
    "/industry", "/industrylanding", "/electronics", "/manufacturing", "/chemical", "/agrochemical",
    "/pharmaceutical", "/retail", "/FMCG", "/fmcg", "/aerospace", "/aeronautical", "/food-beverage",
    "/packaging", "/semiconductor", "/fintech", "/education-edtech", "/sports", "/automobile",
    "/engineering", "/supply", "/pharma",
  ];
  const isIndustriesSectionActive = industryRoutes.some((route) => location.pathname.toLowerCase() === route.toLowerCase());

  const aiRoutes = ["/AI-Machine-Learning-Solution", "/bestoflow", "/predosolutions", "/askochat", "/zentick_ai", "/besto_jyotish"];
  // An AI-category CMS product's page lives under /products, but the product
  // is listed under AI Solutions — so that tab lights up, not Products.
  const isOnAIProductPage = aiProductItems.some((p) => isItemActive(p));
  const isAISolutionsSectionActive =
    isOnAIProductPage || aiRoutes.some((route) => location.pathname.toLowerCase() === route.toLowerCase());

  // The Services landing + the six service-detail routes (Services.js tabs).
  const serviceRoutes = [
    "/bestoservice", "/digital-core-transformation", "/intelligent-industry-solutions",
    "/data-cloud-transformation", "/digital-experience-engagement",
    "/product-engineering-innovation", "/flexible-talent-delivery-models",
  ];
  // Service tabs live one level deeper (/digital-core-transformation/sap-ibp).
  const isServicesSectionActive = serviceRoutes.some((route) => {
    const p = location.pathname.toLowerCase();
    const r = route.toLowerCase();
    return p === r || p.startsWith(`${r}/`);
  });

  // Insights: blogs, news, case studies, podcasts, plus Testimonials & Success
  // Stories. Blog detail pages live at /blogs/:slug — `startsWith("/blogs/")`
  // catches them.
  const isInsightsSectionActive =
    location.pathname === "/blogs" ||
    location.pathname.startsWith("/blogs/") ||
    location.pathname.startsWith("/podcast/") ||
    location.pathname.startsWith("/case-study/") ||
    location.pathname.startsWith("/casestudy/") ||
    location.pathname.startsWith("/news/") ||
    location.pathname === "/testimonials" ||
    location.pathname.startsWith("/testimonial/") ||
    location.pathname.startsWith("/employee-appreciation/") ||
    location.pathname.startsWith("/success-stories");

  // Careers: landing + listing + job-detail pages /details/:jobId.
  // (/careers, /career and the old apply-flow pages redirect to /careerlanding.)
  const careerRoutes = ["/careerlanding", "/intern"];
  const isCareersSectionActive =
    careerRoutes.some((route) => location.pathname.toLowerCase() === route.toLowerCase()) ||
    location.pathname.startsWith("/details/");

  const isProductsPath = location.pathname === "/products" || location.pathname.startsWith("/products/");

  // Section tabs light up for every page inside their section.
  const isTabActive = (name) => {
    if (name === "Industries") return isIndustriesSectionActive;
    if (name === "AI Solutions") return isAISolutionsSectionActive;
    if (name === "Products") return isProductsPath && !isOnAIProductPage;
    if (name === "Insights") return isInsightsSectionActive;
    if (name === "Services") return isServicesSectionActive;
    if (name === "Careers") return isCareersSectionActive;
    const link = resolvedNavLinks.find((l) => l.name === name);
    return !!link && pathIs(link.path);
  };
  const activeTab = resolvedNavLinks.find((l) => isTabActive(l.name))?.name || null;

  // Category a section opens on: the one holding the current page, else the
  // first. Once the user picks one it sticks until the route changes.
  const activeCategory = (link) => {
    const i = link.menu.columns.findIndex((c) => c.items.some((it) => isItemActive(it)));
    return i < 0 ? 0 : i;
  };
  const catOf = (link) => {
    const i = catSel[link.name];
    return i != null && i < link.menu.columns.length ? i : activeCategory(link);
  };
  const selectCat = (name, i) => {
    clearTimeout(catT.current);
    setCatSel((s) => (s[name] === i ? s : { ...s, [name]: i }));
  };
  // Hover selects after a short intent delay, so a diagonal move from the
  // rail toward the detail pane doesn't flip categories on the way.
  const hoverCat = (name, i, e) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(catT.current);
    catT.current = setTimeout(() => selectCat(name, i), 80);
  };
  // ↑/↓ move between categories (vertical tablist).
  const onCatKey = (e, i, count) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const tabs = e.currentTarget.parentElement.querySelectorAll('[role="tab"]');
    tabs[(i + (e.key === "ArrowDown" ? 1 : -1) + count) % count]?.focus();
  };

  /* ---------- open / close ---------- */
  const clearTimers = () => {
    clearTimeout(openT.current);
    clearTimeout(closeT.current);
  };
  const openMenuNow = useCallback((name) => {
    clearTimeout(openT.current);
    clearTimeout(closeT.current);
    setOpenMenu(name);
    setPanelMenu(name);
    // Opening a mega panel dismisses the search sheet — they must never stack.
    setIsSearchOpen(false);
  }, []);
  const closeMenu = useCallback(() => {
    clearTimeout(openT.current);
    clearTimeout(closeT.current);
    setOpenMenu(null);
  }, []);
  const closeAll = useCallback(() => {
    closeMenu();
    setIsSearchOpen(false);
    setIsMobileSearchOpen(false);
    setSheetOpen(false);
  }, [closeMenu]);

  const openSheet = () => {
    // Tablets open on the section you're in; phones start at the list.
    setSheetLevel("root");
    setSheetSection((s) =>
      isTablet ? (menuLinks.some((l) => l.name === activeTab) ? activeTab : menuLinks[0]?.name) : s || menuLinks[0]?.name
    );
    setIsMobileSearchOpen(false);
    setSheetOpen(true);
  };
  const drill = (name) => {
    setSheetSection(name);
    if (!isTablet) {
      setSheetLevel("sec");
      setTimeout(() => sheetRefs.current[name]?.querySelector(".bn-sback")?.focus({ preventScroll: true }), 60);
    }
  };
  const backToSections = () => {
    setSheetLevel("root");
    setTimeout(() => rowRefs.current[sheetSection]?.focus({ preventScroll: true }), 60);
  };

  /* ---------- desktop indicator ---------- */
  // Orange bar under the hovered or open tab; rests on the current section.
  // Positioned directly (no React state) so hover tracking never re-renders.
  const moveInd = useCallback((el, instant = false) => {
    const ind = indRef.current;
    const nav = navRef.current;
    if (!ind || !nav) return;
    const nr = nav.getBoundingClientRect();
    if (!el || !nr.width) {
      ind.style.opacity = "0";
      return;
    }
    const r = el.getBoundingClientRect();
    if (instant) ind.classList.add("bn-no-anim");
    ind.style.opacity = "1";
    ind.style.width = `${r.width - 20}px`;
    ind.style.transform = `translateX(${r.left - nr.left + 10}px)`;
    if (instant) {
      void ind.offsetWidth;
      ind.classList.remove("bn-no-anim");
    }
  }, []);
  const settleInd = useCallback(
    (instant = false) => moveInd(itemRefs.current[openMenu || activeTab], instant),
    [moveInd, openMenu, activeTab]
  );
  useEffect(() => {
    settleInd();
  }, [settleInd]);
  // Place it without animating on load, after fonts settle, and on resize.
  useEffect(() => {
    if (!isDesk) return undefined;
    settleInd(true);
    let alive = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => alive && settleInd(true));
    const onResize = () => settleInd(true);
    window.addEventListener("resize", onResize);
    return () => {
      alive = false;
      window.removeEventListener("resize", onResize);
    };
  }, [isDesk, settleInd]);

  /* ---------- entrance animation ----------
     Runs once on mount. Only transform/opacity are animated (no layout
     shift), inline styles are cleared afterwards, and it respects
     prefers-reduced-motion. */
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.fromTo(headerRef.current, { y: -30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, clearProps: "transform,opacity" }, 0.2);
      tl.fromTo(logoRef.current, { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 0.35, transformOrigin: "left center", clearProps: "transform,opacity" }, 0.35);
      const items = gsap.utils.toArray(".bn-nav li, .bn-actions > *", headerRef.current);
      gsap.set(items, { transition: "none" });
      tl.fromTo(
        items,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.06, onComplete: () => gsap.set(items, { clearProps: "transition,transform,opacity" }) },
        0.45
      );
    }, headerRef);
    return () => ctx.revert();
  }, []);

  /* ---------- global listeners ---------- */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock page scroll while the compact sheet is open.
  useEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  // Crossing the 1200px breakpoint swaps bar ↔ sheet: close everything.
  useEffect(() => {
    closeAll();
  }, [isDesk, closeAll]);
  // Rotating between phone and tablet layouts: restart the sheet at the list.
  useEffect(() => {
    setSheetLevel("root");
  }, [isTablet]);

  // Any navigation closes menus/search and re-derives default categories.
  useEffect(() => {
    closeAll();
    setCatSel({});
  }, [location.pathname, location.search, closeAll]);

  // Escape closes whatever is open (focus returns to its trigger); ⌘K / Ctrl+K
  // toggles site search (the desktop sheet, or the compact search row).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (openMenu) itemRefs.current[openMenu]?.focus();
        else if (sheetOpen) burgerRef.current?.focus();
        closeAll();
        return;
      }
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        closeMenu();
        setSheetOpen(false);
        if (window.matchMedia(DESK_MQ).matches) setIsSearchOpen((v) => !v);
        else setIsMobileSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu, sheetOpen, closeAll, closeMenu]);

  // Pointer down outside the header closes the mega panel / search sheet.
  useEffect(() => {
    if (!openMenu && !isSearchOpen) return undefined;
    const onDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        closeMenu();
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [openMenu, isSearchOpen, closeMenu]);

  // Clear pending hover timers on unmount.
  useEffect(() => {
    const timers = [openT, closeT, catT];
    return () => timers.forEach((t) => clearTimeout(t.current));
  }, []);

  /* ---------- desktop trigger handlers ---------- */
  // Hover opens after a short intent delay (instant when moving between open
  // menus); click/tap/Enter also open. A mouse click never closes an open
  // menu (so hover-then-click doesn't flicker shut); touch and keyboard toggle.
  const triggerProps = (link) => ({
    onPointerDown: (e) => {
      lastPointer.current = e.pointerType;
    },
    onPointerEnter: (e) => {
      moveInd(e.currentTarget);
      if (e.pointerType !== "mouse") return;
      clearTimers();
      openT.current = setTimeout(() => openMenuNow(link.name), openMenu ? 30 : 90);
    },
    onPointerLeave: () => clearTimeout(openT.current),
    onFocus: (e) => moveInd(e.currentTarget),
    onClick: (e) => {
      const keyboard = e.detail === 0;
      if (openMenu === link.name) {
        if (keyboard || lastPointer.current !== "mouse") closeMenu();
      } else openMenuNow(link.name);
    },
    onKeyDown: (e) => {
      if (e.key !== "ArrowDown") return;
      e.preventDefault();
      openMenuNow(link.name);
      setTimeout(() => panelRef.current?.querySelector('[role="tab"][aria-selected="true"], a[href]')?.focus(), 60);
    },
  });
  // Plain links, the search button and the CTA dismiss an open panel.
  const dismissOnHover = (e) => {
    clearTimeout(openT.current);
    if (e && e.currentTarget && e.currentTarget.classList.contains("bn-item")) moveInd(e.currentTarget);
    if (openMenu) {
      clearTimeout(closeT.current);
      closeT.current = setTimeout(closeMenu, 80);
    }
  };

  /* ---------- render helpers ---------- */
  // Link card used in the panel and the sheet; products show their logo.
  const renderCard = (item) => {
    const inner = (
      <>
        {item.logo && <img className="bn-card-logo" src={item.logo} alt="" loading="lazy" />}
        <span>
          {item.name}
          {item.desc && <small>{item.desc}</small>}
        </span>
        <ArrowUpRight size={17} strokeWidth={2} className="bn-ico" aria-hidden="true" />
      </>
    );
    if (item.external) {
      return (
        <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer" className="bn-card" onClick={closeAll}>
          {inner}
        </a>
      );
    }
    const active = isItemActive(item);
    return (
      <Link key={item.path} to={item.path} className={`bn-card${active ? " is-active" : ""}`} aria-current={active ? "page" : undefined} onClick={closeAll}>
        {inner}
      </Link>
    );
  };
  const landingLink = (link) => (
    <Link to={link.path} className="bn-arrow-link" onClick={closeAll}>
      {link.menu.landingLabel}
      <ArrowRight size={15} strokeWidth={2} className="bn-ico" aria-hidden="true" />
    </Link>
  );
  const idOf = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const inertIf = (cond) => (cond ? "" : undefined); // React 18: `inert` as an attribute

  const panelLink = resolvedNavLinks.find((l) => l.name === panelMenu && l.menu);

  return (
    <div className="bn-root">
      {/* Spacer to offset the fixed header. Skipped on the home page, where the
          hero starts flush at the top so the frosted bar overlays the video.
          Height = --header-h (index.css), shared with sticky page bars. */}
      {location.pathname !== "/" && <div className="bn-spacer" aria-hidden="true" />}

      <header
        ref={headerRef}
        className={`bn${isScrolled ? " is-scrolled" : ""}${openMenu ? " has-panel" : ""}`}
        onPointerEnter={() => clearTimeout(closeT.current)}
        onPointerLeave={(e) => {
          if (e.pointerType !== "mouse") return;
          // Browsers occasionally fire a leave while the pointer is still over
          // the header (e.g. when content under it re-renders); ignore those so
          // an open panel never closes under the user's cursor.
          const under = document.elementFromPoint(e.clientX, e.clientY);
          if (under && e.currentTarget.contains(under)) return;
          clearTimeout(openT.current);
          closeT.current = setTimeout(closeMenu, 170);
        }}
        onBlur={(e) => {
          if (openMenu && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) closeMenu();
        }}
      >
        <div className="bn-wrap bn-bar">
          <NavLink
            to="/"
            className="bn-logo"
            aria-label="Bestowal Systems and Services, home"
            onClick={() => {
              closeAll();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onPointerEnter={dismissOnHover}
          >
            <img ref={logoRef} src={Headerlogo} alt="Bestowal Systems and Services" width="188" height="40" />
          </NavLink>

          {/* Desktop nav (>= 1200px) */}
          <nav ref={navRef} className="bn-nav bn-desk" aria-label="Primary" onPointerLeave={() => settleInd()}>
            <ul>
              {resolvedNavLinks.map((link) => (
                <li key={link.name}>
                  {link.menu ? (
                    <button
                      ref={(el) => (itemRefs.current[link.name] = el)}
                      type="button"
                      className={`bn-item${activeTab === link.name ? " is-current" : ""}`}
                      aria-haspopup="true"
                      aria-expanded={openMenu === link.name}
                      aria-controls="bn-panel"
                      {...triggerProps(link)}
                    >
                      {link.name}
                      <ChevronDown size={15} strokeWidth={2} className="bn-ico bn-chev" aria-hidden="true" />
                    </button>
                  ) : (
                    <NavLink
                      ref={(el) => (itemRefs.current[link.name] = el)}
                      to={link.path}
                      className={`bn-item${activeTab === link.name ? " is-current" : ""}`}
                      aria-current={activeTab === link.name ? "page" : undefined}
                      onPointerEnter={dismissOnHover}
                      onFocus={(e) => moveInd(e.currentTarget)}
                      onClick={closeAll}
                    >
                      {link.name}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
            <span ref={indRef} className="bn-ind" aria-hidden="true" />
          </nav>

          <div className="bn-actions">
            <button
              type="button"
              className="bn-icon-btn"
              aria-label="Search"
              aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
              aria-expanded={isDesk ? isSearchOpen : isMobileSearchOpen}
              onPointerEnter={dismissOnHover}
              onClick={() => {
                closeMenu();
                if (isDesk) setIsSearchOpen((v) => !v);
                else {
                  setSheetOpen(false);
                  setIsMobileSearchOpen((v) => !v);
                }
              }}
            >
              <Search size={20} strokeWidth={2} className="bn-ico" aria-hidden="true" />
            </button>
            <NavLink to={CTA.path} className="bn-cta bn-tab-up" onPointerEnter={dismissOnHover} onClick={closeAll}>
              {CTA.label}
              <ArrowRight size={16} strokeWidth={2} className="bn-ico" aria-hidden="true" />
            </NavLink>
            <button
              ref={burgerRef}
              type="button"
              className="bn-burger bn-compact"
              aria-label={sheetOpen ? "Close menu" : "Open menu"}
              aria-expanded={sheetOpen}
              aria-controls="bn-sheet"
              onClick={() => (sheetOpen ? setSheetOpen(false) : openSheet())}
            >
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Desktop mega panel: category rail | detail | image */}
        <div id="bn-panel" ref={panelRef} className={`bn-panel bn-desk${openMenu ? " is-open" : ""}`}>
          {panelLink &&
            (() => {
              const cols = panelLink.menu.columns;
              const cat = catOf(panelLink);
              const col = cols[cat];
              const id = idOf(panelLink.name);
              return (
                <div className="bn-sec bn-wrap">
                  <div className="bn-list">
                    {/* Only tabs may live in a tablist; the landing link sits after it. */}
                    <div role="tablist" aria-orientation="vertical" aria-label={panelLink.name}>
                    {cols.map((c, i) => (
                      <button
                        key={c.title}
                        type="button"
                        role="tab"
                        id={`bn-tab-${id}-${i}`}
                        aria-selected={i === cat}
                        aria-controls={`bn-pane-${id}`}
                        tabIndex={i === cat ? 0 : -1}
                        className="bn-cat"
                        onPointerEnter={(e) => hoverCat(panelLink.name, i, e)}
                        onPointerLeave={() => clearTimeout(catT.current)}
                        onClick={() => selectCat(panelLink.name, i)}
                        onFocus={() => selectCat(panelLink.name, i)}
                        onKeyDown={(e) => onCatKey(e, i, cols.length)}
                      >
                        {c.title}
                        <ChevronRight size={16} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                      </button>
                    ))}
                    </div>
                    {landingLink(panelLink)}
                  </div>
                  <div className="bn-detail">
                    <div key={`${panelLink.name}-${cat}`} className="bn-pane" role="tabpanel" id={`bn-pane-${id}`} aria-labelledby={`bn-tab-${id}-${cat}`}>
                      <h3>{col.title}</h3>
                      {col.desc && <p>{col.desc}</p>}
                      <div className="bn-links">{col.items.map(renderCard)}</div>
                    </div>
                  </div>
                  <div className="bn-media">
                    <div className={panelLink.menu.wideMedia ? "bn-imgs bn-imgs--wide" : "bn-imgs"}>
                      {cols.map((c, i) =>
                        c.img ? <img key={c.title} src={c.img} alt="" loading="lazy" className={i === cat ? "is-active" : undefined} /> : null
                      )}
                    </div>
                    <p className="bn-help">
                      Not sure which fits?{" "}
                      <Link to={CTA.path} onClick={closeAll}>
                        {CTA.label}
                        <ArrowRight size={14} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                      </Link>
                    </p>
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Desktop search sheet (full width under the bar) */}
        <AnimatePresence>
          {isDesk && isSearchOpen && <DesktopSearch onClose={() => setIsSearchOpen(false)} />}
        </AnimatePresence>

        {/* Compact search row — drops below the bar (< 1200px) */}
        <AnimatePresence>
          {!isDesk && isMobileSearchOpen && (
            <motion.div
              key="bn-msearch"
              className="bn-msearch bn-compact"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <SearchBox autoFocus placeholder="Search anything…" onClose={() => setIsMobileSearchOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Compact menu sheet (< 1200px). A sibling of the header so its
          position:fixed resolves against the viewport (the header carries a
          GSAP transform during its entrance). */}
      <div id="bn-sheet" className={`bn-sheet bn-compact${sheetOpen ? " is-open" : ""}`} data-level={sheetLevel}>
        <div className="bn-sheet-inner" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="bn-sheet-body">
            <div className="bn-sa" inert={inertIf(!isTablet && sheetLevel === "sec")}>
              <ul className="bn-sroot">
                {resolvedNavLinks.map((link, i) => {
                  const current = activeTab === link.name;
                  return (
                    <li key={link.name} style={{ "--i": i }}>
                      {link.menu ? (
                        <button
                          ref={(el) => (rowRefs.current[link.name] = el)}
                          type="button"
                          className={`bn-srow${current ? " is-current" : ""}${isTablet && sheetSection === link.name ? " is-selected" : ""}`}
                          aria-current={isTablet ? (sheetSection === link.name ? "true" : "false") : undefined}
                          onClick={() => drill(link.name)}
                        >
                          <span>
                            <b>{link.name}</b>
                            {link.menu.blurb && <small>{link.menu.blurb}</small>}
                          </span>
                          <ChevronRight size={18} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                        </button>
                      ) : (
                        <NavLink to={link.path} className={`bn-srow${current ? " is-current" : ""}`} aria-current={current ? "page" : undefined} onClick={closeAll}>
                          <span>
                            <b>{link.name}</b>
                          </span>
                          <ArrowUpRight size={17} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="bn-sfoot">
                <NavLink to={CTA.path} className="bn-cta" onClick={closeAll}>
                  {CTA.label}
                  <ArrowRight size={16} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                </NavLink>
                <p>
                  {/* <wbr> lets a narrow column break the address after "@" */}
                  <a href={`mailto:${CONTACT.email}`}>
                    {CONTACT.email.split("@")[0]}@<wbr />
                    {CONTACT.email.split("@")[1]}
                  </a>
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}>{CONTACT.phone}</a>
                </p>
              </div>
            </div>

            {menuLinks.map((link) => {
              const cols = link.menu.columns;
              const cat = catOf(link);
              const col = cols[cat];
              const id = idOf(link.name);
              const shown = sheetSection === link.name;
              return (
                <section
                  key={link.name}
                  ref={(el) => (sheetRefs.current[link.name] = el)}
                  className={`bn-ssec${shown ? " is-active" : ""}`}
                  aria-label={link.name}
                  inert={inertIf(!shown || (!isTablet && sheetLevel !== "sec"))}
                >
                  <div className="bn-shead">
                    <button type="button" className="bn-sback" onClick={backToSections}>
                      <ChevronLeft size={18} strokeWidth={2} className="bn-ico" aria-hidden="true" />
                      Menu
                    </button>
                    <div className="bn-stitle">
                      <h2>{link.name}</h2>
                      {link.menu.blurb && <p>{link.menu.blurb}</p>}
                    </div>
                    {landingLink(link)}
                  </div>
                  <div className="bn-ssplit">
                    <div className="bn-srail" role="tablist" aria-orientation="vertical" aria-label={`${link.name} categories`}>
                      {cols.map((c, i) => {
                        const Icon = c.icon;
                        return (
                          <button
                            key={c.title}
                            type="button"
                            role="tab"
                            id={`bn-stab-${id}-${i}`}
                            aria-selected={i === cat}
                            aria-controls={`bn-spane-${id}`}
                            tabIndex={i === cat ? 0 : -1}
                            className="bn-scat"
                            onClick={(e) => {
                              selectCat(link.name, i);
                              e.currentTarget.closest(".bn-ssplit")?.querySelector(".bn-sdetail")?.scrollTo(0, 0);
                            }}
                            onFocus={() => selectCat(link.name, i)}
                            onKeyDown={(e) => onCatKey(e, i, cols.length)}
                          >
                            {Icon && <Icon size={20} strokeWidth={1.75} className="bn-ico" aria-hidden="true" />}
                            <span>{c.title}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="bn-sdetail">
                      <div key={`${link.name}-${cat}`} className="bn-spane" role="tabpanel" id={`bn-spane-${id}`} aria-labelledby={`bn-stab-${id}-${cat}`}>
                        {col.img && (
                          <div className={link.menu.wideMedia ? "bn-simg bn-simg--wide" : "bn-simg"}>
                            <img src={col.img} alt="" loading="lazy" />
                          </div>
                        )}
                        <div className="bn-stext">
                          <h3>{col.title}</h3>
                          {col.desc && <p>{col.desc}</p>}
                          <div className="bn-slinks">{col.items.map(renderCard)}</div>
                        </div>
                      </div>
                      {landingLink(link)}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
